import { useState } from 'react'
import { Camera, Loader2, ScanLine } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { CameraCardScanner } from '@/components/counter/loyaltyCardScanner'
import { cameraScanSupported, createBarcodeDecoder } from '@/lib/scannerCamera'
import { canonicalBarcode, isPlausibleScan } from '@/lib/barcode'
import { IM } from './itemMasterStrings'

/**
 * Camera + typed-number barcode entry for the store (kitchen flow). USB guns
 * do not need this dialog — useKeyboardWedgeScanner catches them anywhere on
 * the page — so this is the phone/tablet path and the "type the digits under
 * the bars" fallback. Emits a canonical code; the caller resolves it.
 */
export function BarcodeScanDialog({ open, onClose, onCode, busy }: {
  open: boolean
  onClose: () => void
  onCode: (code: string) => void
  busy?: boolean
}) {
  const [typed, setTyped] = useState('')
  const [camera, setCamera] = useState(() => cameraScanSupported())
  const [cameraKey, setCameraKey] = useState(0)
  const [badRead, setBadRead] = useState(false)

  function submit(raw: string) {
    const code = canonicalBarcode(raw)
    if (!code) {
      setBadRead(true)
      return
    }
    setBadRead(false)
    setTyped('')
    onCode(code)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanLine className="h-4 w-4" /> {IM.scan.title}</DialogTitle>
          <DialogDescription>{IM.scan.wedgeTip}</DialogDescription>
        </DialogHeader>

        {camera ? (
          <CameraCardScanner
            key={cameraKey}
            busy={Boolean(busy)}
            decoderFactory={createBarcodeDecoder}
            hint={IM.scan.cameraHint}
            frame="wide"
            onClose={() => setCamera(false)}
            onDecode={(value) => {
              // A garbled EAN fails its check digit — keep scanning instead of
              // looking up a code that is not on the pack.
              if (!isPlausibleScan(value.trim())) {
                setBadRead(true)
                setCameraKey((k) => k + 1)
                return
              }
              submit(value)
            }}
          />
        ) : (
          cameraScanSupported() && (
            <Button type="button" variant="outline" className="w-full" onClick={() => { setCamera(true); setCameraKey((k) => k + 1) }}>
              <Camera className="mr-2 h-4 w-4" /> {IM.scan.button}
            </Button>
          )
        )}

        <form
          className="space-y-2"
          onSubmit={(e) => { e.preventDefault(); submit(typed) }}
        >
          <label className="block text-sm font-medium" htmlFor="barcode-typed">{IM.scan.typeLabel}</label>
          <div className="flex gap-2">
            <input
              id="barcode-typed"
              inputMode="numeric"
              autoComplete="off"
              className="h-10 min-w-0 flex-1 rounded-md border bg-background px-3 text-base tracking-wider tabular-nums"
              value={typed}
              onChange={(e) => { setTyped(e.target.value); setBadRead(false) }}
            />
            <Button type="submit" disabled={!typed.trim() || busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : IM.scan.find}
            </Button>
          </div>
          {badRead && <p className="text-sm text-danger-fg">{IM.scan.badRead}</p>}
        </form>
      </DialogContent>
    </Dialog>
  )
}
